import { acceptHMRUpdate, defineStore } from 'pinia';
import { http } from '@/services/http.service';

export type FieldOperation = 'public' | 'private';

export interface Field {
  id?: number;
  slug?: string;
  name: string;
  description?: string | null;
  lat?: number | null;
  lon?: number | null;
  image_url?: string | null;
  operation: FieldOperation | 'public_operation' | 'private_operation';
  quality_score?: number; // 0..100
  votes_count?: number;
  sport_id?: number | null;
}

interface FieldsState {
  items: Field[];
  activePlayersByField: Record<
    string,
    Array<{ id: number; email: string | null; status: 'active' | 'inactive' | string }>
  >;
  loading: boolean;
  error: string;
}

export const useFieldsStore = defineStore('fields.store', {
  state: (): FieldsState => ({ items: [], activePlayersByField: {}, loading: false, error: '' }),
  getters: {
    byId: (state) => (id: number) => state.items.find((f) => f.id === id) || null,
    bySlug: (state) => (slug: string) => state.items.find((f) => f.slug === slug) || null,
    activePlayersFor: (state) => (fieldIdOrSlug: number | string) =>
      state.activePlayersByField[String(fieldIdOrSlug)] || [],
    visitsById: (state) => (id: number) => {
      const f = state.items.find((x) => x.id === id) as
        | (Field & { visits_count?: number })
        | undefined;
      return f?.visits_count ?? 0;
    },
  },
  actions: {
    // No-op realtime initializer to keep compatibility with legacy components
    // Realtime via websockets is disabled; data loads only on demand.
    initRealtime(): void {
      // intentionally empty
    },
    async fetchAll(): Promise<Field[]> {
      this.loading = true;
      this.error = '';
      try {
        const res = await http.get('/api/v1/fields');
        this.items = Array.isArray(res.data) ? res.data : [];
        return this.items;
      } catch (e: unknown) {
        const message =
          typeof e === 'object' && e && 'message' in e
            ? String((e as { message?: unknown }).message)
            : '';
        this.error = message || 'Error cargando canchas';
        this.items = [];
        return [];
      } finally {
        this.loading = false;
      }
    },

    async fetchBySlug(slug: string): Promise<Field | null> {
      this.loading = true;
      this.error = '';
      try {
        const res = await http.get(`/api/v1/fields/slug/${encodeURIComponent(slug)}`);
        return res.data as Field;
      } catch (e: unknown) {
        const message =
          typeof e === 'object' && e && 'message' in e
            ? String((e as { message?: unknown }).message)
            : '';
        this.error = message || 'No se encontró la cancha';
        return null;
      } finally {
        this.loading = false;
      }
    },

    // Poll the entire fields list periodically
    startPolling(intervalMs = 5000) {
      // @ts-expect-error store instance dynamic property
      if (this.__poller) return;
      // Immediate fetch, then interval
      this.fetchAll();
      // @ts-expect-error store instance dynamic property
      this.__poller = setInterval(
        () => {
          this.fetchAll().catch(() => {});
        },
        Math.max(1000, intervalMs),
      );
    },

    stopPolling() {
      // @ts-expect-error store instance dynamic property
      if (this.__poller) {
        // @ts-expect-error store instance dynamic property
        clearInterval(this.__poller as number);
        // @ts-expect-error store instance dynamic property
        this.__poller = null;
      }
    },

    // Poll a single field by id or slug and merge into items
    startPollingField(identifier: number | string, intervalMs = 4000) {
      const key = String(identifier);
      // @ts-expect-error dynamic property map
      this.__fieldPollers ||= {} as Record<string, number | null>;
      // @ts-expect-error dynamic property map
      if (this.__fieldPollers[key]) return;

      const fetchOne = async () => {
        try {
          const isNumeric = !isNaN(Number(identifier)) && String(Number(identifier)) === key;
          const url = isNumeric
            ? `/api/v1/fields/${encodeURIComponent(key)}`
            : `/api/v1/fields/slug/${encodeURIComponent(key)}`;
          const res = await http.get(url);
          const item = res.data as Field & { id?: number };
          if (item && item.id) {
            const idx = this.items.findIndex((i) => i.id === item.id);
            if (idx !== -1) this.items[idx] = item;
            else this.items.push(item);
          }
        } catch (e) {
          // ignore
        }
      };

      fetchOne();
      // @ts-expect-error dynamic property map
      this.__fieldPollers[key] = setInterval(
        fetchOne,
        Math.max(1000, intervalMs),
      ) as unknown as number;
    },

    stopPollingField(identifier: number | string) {
      const key = String(identifier);
      // @ts-expect-error dynamic property map
      if (this.__fieldPollers && this.__fieldPollers[key]) {
        // @ts-expect-error dynamic property map
        clearInterval(this.__fieldPollers[key] as number);
        // @ts-expect-error dynamic property map
        this.__fieldPollers[key] = null;
      }
    },

    async create(data: Field): Promise<Field | null> {
      try {
        const res = await http.post('/api/v1/fields', { field: data });
        const item = res.data as Field;
        this.items.push(item);
        return item;
      } catch (error) {
        console.error('Error creating field:', error);
        throw error;
      }
    },

    async update(id: number, data: Partial<Field>): Promise<Field | null> {
      try {
        const res = await http.put(`/api/v1/fields/${id}`, { field: data });
        const updated = res.data as Field;
        const idx = this.items.findIndex((i) => i.id === id);
        if (idx !== -1) this.items[idx] = updated;
        return updated;
      } catch (error) {
        console.error('Error updating field:', error);
        throw error;
      }
    },

    async remove(id: number): Promise<void> {
      try {
        await http.delete(`/api/v1/fields/${id}`);
        this.items = this.items.filter((i) => i.id !== id);
      } catch (error) {
        console.error('Error deleting field:', error);
        throw error;
      }
    },

    async vote(fieldIdOrSlug: number | string, value: number): Promise<Field | null> {
      try {
        const id = encodeURIComponent(String(fieldIdOrSlug));
        const res = await http.post(`/api/v1/fields/${id}/votes`, { vote: { value } });
        const updated = res.data as Field;
        const idx = this.items.findIndex((i) => i.id === updated.id);
        if (idx !== -1) this.items[idx] = updated;
        return updated;
      } catch (error) {
        console.error('Error voting field:', error);
        throw error;
      }
    },

    async activatePlayerField(
      fieldIdOrSlug: number | string,
      userPayload?: Record<string, string>,
    ): Promise<unknown> {
      try {
        const id = encodeURIComponent(String(fieldIdOrSlug));
        const res = await http.post(
          `/api/v1/fields/${id}/field_players`,
          userPayload ? { user: userPayload } : {},
        );
        return res.data;
      } catch (error) {
        console.error('Error activating player in field:', error);
        throw error;
      }
    },

    async togglePlayerField(
      fieldIdOrSlug: number | string,
    ): Promise<{ status: 'active' | 'inactive' }> {
      try {
        const id = encodeURIComponent(String(fieldIdOrSlug));
        const res = await http.patch(`/api/v1/fields/${id}/field_players/toggle`);
        return res.data as { status: 'active' | 'inactive' };
      } catch (error) {
        console.error('Error toggling player status:', error);
        throw error;
      }
    },

    async fetchActivePlayers(
      fieldIdOrSlug: number | string,
    ): Promise<Array<{ id: number; email: string | null; status: string }>> {
      try {
        const id = encodeURIComponent(String(fieldIdOrSlug));
        const res = await http.get(`/api/v1/fields/${id}/field_players`);
        const players = res.data as Array<{ id: number; email: string | null; status: string }>;
        this.activePlayersByField[String(fieldIdOrSlug)] = Array.isArray(players) ? players : [];
        return this.activePlayersByField[String(fieldIdOrSlug)];
      } catch (error) {
        console.error('Error fetching active players:', error);
        this.activePlayersByField[String(fieldIdOrSlug)] = [];
        return [];
      }
    },

    async loadActivePlayers(fieldIdOrSlug: number | string): Promise<void> {
      await this.fetchActivePlayers(fieldIdOrSlug);
    },

    async verifyPlayerOtp(
      fieldIdOrSlug: number | string,
      payload: { email: string; otp_code: string },
    ): Promise<unknown> {
      try {
        const id = encodeURIComponent(String(fieldIdOrSlug));
        const res = await http.post(`/api/v1/fields/${id}/field_players/verify_otp`, {
          user: payload,
        });
        return res.data;
      } catch (error) {
        console.error('Error verifying OTP:', error);
        throw error;
      }
    },

    async fetchMyPlayerFieldStatus(
      fieldIdOrSlug: number | string,
    ): Promise<{ status: 'active' | 'inactive' }> {
      try {
        const id = encodeURIComponent(String(fieldIdOrSlug));
        const res = await http.get(`/api/v1/fields/${id}/field_players/me`);
        return res.data as { status: 'active' | 'inactive' };
      } catch (error) {
        console.error('Error fetching my player status:', error);
        throw error;
      }
    },
  },
});

// HMR support
if (import.meta && 'hot' in import.meta) {
  import.meta.hot?.accept(acceptHMRUpdate(useFieldsStore, import.meta as ImportMeta));
}
